import {
  Badge,
  Button,
  Flex,
  Group,
  Stack,
  Text,
  Title,
  useMantineTheme,
  Avatar,
  Tooltip
} from '@mantine/core'
import { IconTrendingDown, IconTrendingUp } from '@tabler/icons'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import PropTypes from 'prop-types'
import React from 'react'
import ImageFallback from '../image_fallback'

const ChartsCard = ({ data, currentPlatform, isTop50, showPosition = true }) => {
  const theme = useMantineTheme()
  const t = useTranslations('Charts')
  const t_genres = useTranslations('Genres')
  const t_language = useTranslations('PrimaryCountryCode')
  const [isHovered, setIsHovered] = React.useState(false)

  const calculatePosition = (previousPosition) => {
    if (previousPosition == null || previousPosition > 20) return { text: 'New' }
    if (previousPosition === 0) return { text: '-' }
    return previousPosition > 0
      ? { text: 'Up', amount: previousPosition.toString() }
      : { text: 'Down', amount: Math.abs(previousPosition).toString() }
  }

  const hitsPosition = calculatePosition(data.previousPosition)

  const getContentType = (type) => {
    if (type === 'Movies') return 'movie'
    if (type === 'TV Shows') return 'series'
    return type
  }

  const renderBadge = () => {
    return (
      <Tooltip
        label="En relación Hits Global"
        position="top"
        disabled={
          !isTop50 || hitsPosition?.text === 'New' || hitsPosition?.text === '-'
        }
        sx={(theme) => ({
          backgroundColor: '#1C2B37',
          padding: '4px 8px',
          fontSize: '12px',
          borderRadius: '4px',
          color: theme.white
        })}
        withArrow
        arrowSize={6}
      >
        <Badge
          variant="filled"
          onMouseEnter={() => isTop50 && setIsHovered(true)}
          onMouseLeave={() => isTop50 && setIsHovered(false)}
          sx={(theme) => ({
            width: '75px',
            height: '35px',
            fontSize: '15px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '4px',
            whiteSpace: 'nowrap',
            backgroundColor:
              hitsPosition?.text === 'Up'
                ? '#369D4D'
                : hitsPosition?.text === 'Down'
                ? '#B24747'
                : '#1C2B37CC',
            color: theme.white,
            '& .number': {
              transition: 'opacity 0.3s ease-in-out',
              opacity: 1
            },
            [theme.fn.smallerThan('sm')]: {
              width: '60px',
              height: '25px',
              fontSize: '10px'
            }
          })}
          leftSection={
            hitsPosition?.text !== 'New' &&
            hitsPosition?.text !== '-' &&
            (hitsPosition?.text === 'Up' ? (
              <IconTrendingUp size={16} style={{ color: '#29CE3A' }} />
            ) : (
              <IconTrendingDown size={16} style={{ color: '#FF4C51' }} />
            ))
          }
        >
          {hitsPosition?.text === 'New' || hitsPosition?.text === '-' ? (
            hitsPosition?.text
          ) : isTop50 ? (
            <>
              {hitsPosition?.text !== 'New' && (
                <span className="number">{hitsPosition?.amount}</span>
              )}
            </>
          ) : (
            hitsPosition?.amount
          )}
        </Badge>
      </Tooltip>
    )
  }

  return (
    <Stack
      spacing={0}
      sx={(theme) => ({
        maxWidth: '1366px',
        width: '80vw',
        margin: '0 auto',
        [theme.fn.smallerThan('sm')]: {
          width: '100%',
          margin: '0'
        }
      })}
    >
      <Link
        href={`/${getContentType(data.type)}/${data.id ? data.id : data.uid}`}
        passHref
      >
        <Flex
          gap="md"
          align="center"
          direction="row"
          sx={{
            textDecoration: 'none'
          }}
        >
          {/* Posición e Imagen */}
          <Group
            sx={(theme) => ({
              width: '137px',
              height: '197px',
              position: 'relative',
              [theme.fn.smallerThan('sm')]: {
                width: '100px',
                height: '150px'
              }
            })}
          >
            {showPosition && (
              <Text
                size={90}
                weight={700}
                color="#7B39B6"
                sx={(theme) => ({
                  position: 'absolute',
                  zIndex: 0,
                  top: '91px',
                  letterSpacing: '-15px',
                  left: data.position > 9 ? '-70px' : '-40px',
                  [theme.fn.smallerThan('sm')]: {
                    top: '81px',
                    letterSpacing: '-10px',
                    left: data.position > 9 ? '-45px' : '-25px',
                    fontSize: '60px'
                  }
                })}
              >
                {data.position}
              </Text>
            )}
            <ImageFallback
              src={`https://wsrv.nl/?url=${encodeURIComponent(
                data.imageUrl
              )}&w=280&h=444&fit=cover&output=webp&ll`}
              fallbackSrc="/noimage.png"
              fill
              sx={{
                position: 'absolute',
                zIndex: 1,
                borderRadius: '4px',
                objectFit: 'cover'
              }}
              alt={`${data.title} poster`}
            />
          </Group>

          {/* Badge */}
          {renderBadge()}

          {/* Información */}
          <Stack spacing="xs" align="flex-start" sx={{ flex: 1 }}>
            <Title
              order={5}
              color="white"
              sx={(theme) => ({
                [theme.fn.smallerThan('sm')]: {
                  fontSize: theme.fontSizes.sm
                }
              })}
            >
              {data.title.toUpperCase()}
            </Title>

            <Group
              spacing="xs"
              sx={(theme) => ({
                [theme.fn.smallerThan('sm')]: {
                  display: 'none'
                }
              })}
            >
              <Text
                sx={{
                  backgroundColor: theme.colors.primary,
                  color: theme.white,
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontWeight: 400,
                  fontSize: theme.fontSizes.xs
                }}
              >
                {t_genres(data.genre).toUpperCase()}
              </Text>
              <Text
                sx={{
                  backgroundColor: theme.colors.primary,
                  color: theme.white,
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontWeight: 400,
                  fontSize: theme.fontSizes.xs
                }}
              >
                {t_language(data.primaryCountry).toUpperCase()}
              </Text>
              <Text
                sx={{
                  backgroundColor: theme.colors.primary,
                  color: theme.white,
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontWeight: 400,
                  fontSize: theme.fontSizes.xs
                }}
              >
                {t(data.type).toUpperCase()}
              </Text>
            </Group>
            <Group>
              {isTop50 ? (
                <Avatar.Group spacing="sm" max={4}>
                  {data.availability.map((item) =>
                    item.name === currentPlatform ? (
                      <Avatar
                        key={item.name}
                        src={`https://wsrv.nl/?url=${item.logo}&w=100&output=webp&ll`}
                        alt={item.name}
                        radius={4}
                        sx={{ width: 40, height: 40 }}
                      />
                    ) : null
                  )}
                  {data.availability.filter((item) => item.name !== currentPlatform)
                    .length > 0 && (
                    <Avatar key="more" radius={4} sx={{ width: 40, height: 40 }}>
                      +
                      {
                        data.availability.filter(
                          (item) => item.name !== currentPlatform
                        ).length
                      }
                    </Avatar>
                  )}
                </Avatar.Group>
              ) : (
                <Avatar.Group spacing="sm" max={3}>
                  {(data?.availability || []).map(
                    (item, index) =>
                      index < 3 && (
                        <Avatar
                          key={item.name}
                          src={`https://wsrv.nl/?url=${item.logo}&w=100&output=webp&ll`}
                          alt={item.name}
                          radius={4}
                          sx={{ width: 40, height: 40 }}
                        />
                      )
                  )}
                  {(data?.availability?.length || 0) > 3 && (
                    <Avatar key="more" radius={4} sx={{ width: 40, height: 40 }}>
                      +{(data?.availability?.length || 0) - 3}
                    </Avatar>
                  )}
                </Avatar.Group>
              )}
            </Group>
          </Stack>
          {/* Botón en pantallas grandes */}
          <Button
            sx={(theme) => ({
              width: '158px',
              height: '46px',
              marginLeft: 'auto',
              fontSize: '10px',
              backgroundColor: '#7B39B6',
              [theme.fn.smallerThan('sm')]: {
                display: 'none'
              }
            })}
            size="md"
            variant="filled"
          >
            {t('viewMore')}
          </Button>
        </Flex>
      </Link>
    </Stack>
  )
}

ChartsCard.propTypes = {
  data: PropTypes.shape({
    imageUrl: PropTypes.string,
    position: PropTypes.number.isRequired,
    previousPosition: PropTypes.number,
    title: PropTypes.string.isRequired,
    genre: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    primaryCountry: PropTypes.string.isRequired,
    releaseDate: PropTypes.string.isRequired,
    availability: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        logo: PropTypes.string.isRequired
      })
    ).isRequired
  }).isRequired,
  currentPlatform: PropTypes.string.isRequired,
  isTop50: PropTypes.bool,
  showPosition: PropTypes.bool
}

export default ChartsCard
